import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Form, Icon } from "semantic-ui-react";

import { Can, useSelectedLocation } from "components";
import { Element } from "components/ReactScroll/index";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, ROLES } from "models";
import { ImageUpload } from "modules/LocationOverviewDashboard/components/General/components";
import { useLanguagesQuery } from "modules/LocationOverviewDashboard/hooks";
import { areIpAddressesValid, getInvalidIpAddressLine } from "modules/LocationOverviewDashboard/utils";
import { countryDropdownOptions } from "utils/common";

import type { LocationDetailFormPayload } from "modules/LocationOverviewDashboard/hooks";

const FormHelper = createFormHelper<LocationDetailFormPayload>();

type GeneralProps = {
  tabId: string;
};

export const General = ({ tabId }: GeneralProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { selectedLocation } = useSelectedLocation();
  const { data: languageOptions = [] } = useLanguagesQuery();

  const { watch, setValue, formState, resetField } = FormHelper.useFormContext();
  const [name, commercialName, country, languageCode, ipAddressEnabled, ipAddresses] = watch([
    "name",
    "commercial_name",
    "country",
    "language_code",
    "ip_address_enabled",
    "ip_addresses"
  ]);

  const locationIpAddressEnabled = selectedLocation?.ip_address_enabled ?? false;
  const locationIpAddresses = locationIpAddressEnabled ? (selectedLocation?.ip_addresses?.join("\n") ?? "") : "";

  useEffect(() => {
    resetField("ip_address_enabled", { defaultValue: locationIpAddressEnabled });
    resetField("ip_addresses", { defaultValue: locationIpAddresses });
  }, [locationIpAddressEnabled, locationIpAddresses]);

  const isNameDisabled = !user || user.role_id > ROLES.SupportAdmin;
  const hasIpAddressError = formState.isSubmitted && ipAddressEnabled && !areIpAddressesValid(ipAddresses);
  const invalidIpAddressLine = getInvalidIpAddressLine(ipAddresses);

  return (
    <Element name={tabId} className="location-overview-section">
      <div className="section-title">
        <div>
          <Icon className="gears" />
        </div>
        <span className="section-title-text">{t("v8_general", "General")}</span>
      </div>
      <div className="location-overview-subheading">{t("v8_location_info", "Location Info")}</div>
      <div className="location-overview-columns">
        <div className="location-overview-column">
          <Form.Field required error={formState.isSubmitted && !name}>
            <label>{t("v8_location_name", "Location Name")}</label>
            <FormHelper.Field.Input name="name" inputProps={{ disabled: isNameDisabled }} />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_location_header", "Location header")}</label>
            <FormHelper.Field.Input name="headline" />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_location_footer", "Location footer")}</label>
            <FormHelper.Field.Input name="footerline" />
          </Form.Field>
          <Form.Field required error={formState.isSubmitted && !country}>
            <label>{t("v8_country", "Country")}</label>
            <FormHelper.Field.Dropdown name="country" options={countryDropdownOptions} dropdownProps={{ search: true }} />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_city", "City")}</label>
            <FormHelper.Field.Input name="city" />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_street", "Street")}</label>
            <FormHelper.Field.Input name="street" />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_postal_code", "Postal code")}</label>
            <FormHelper.Field.Input name="postal_code" />
          </Form.Field>
          <Form.Field>
            <label>{t("v8_claire_v8", "Claire V8")}</label>
            <FormHelper.Field.Checkbox name="redirect_to_new_web_version" label={t("v8_redirect_users_to_v8", "Redirect users to V8")} />
          </Form.Field>
        </div>

        <div className="location-overview-column">
          <Form.Field>
            <label>{t("v8_phone", "Phone")}</label>
            <FormHelper.Field.Input name="phone_nr" />
          </Form.Field>
          <Form.Field required error={formState.isSubmitted && !languageCode}>
            <label>{t("v8_language", "Language")}</label>
            <FormHelper.Field.Dropdown name="language_code" options={languageOptions} dropdownProps={{ search: true }} />
          </Form.Field>
          <Form.Field required error={formState.isSubmitted && !commercialName}>
            <label>{t("v8_commercial_name", "Commercial name")}</label>
            <FormHelper.Field.Input name="commercial_name" />
          </Form.Field>
          <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.Logos}>
            <Form.Field>
              <label>{t("v8_upload_logo", "Upload Logo")} (.jpg, .jpeg, .png)</label>
              <ImageUpload fieldName="logo" label={t("v8_logo", "Logo")} inputId="logo-upload" />
            </Form.Field>
          </Can>
          <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.Files}>
            <Form.Field>
              <label>{t("v8_upload_company_stamp", "Upload Company Stamp")} (.jpg, .jpeg, .png)</label>
              <ImageUpload fieldName="company_stamp" label={t("v8_company_stamp", "Company Stamp")} inputId="company-stamp-upload" />
            </Form.Field>
          </Can>
        </div>
      </div>
      <Divider />

      <div className="location-overview-columns">
        <div className="location-overview-column">
          <div className="location-overview-subheading">{t("v8_ip_address", "IP Address")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox
              name="ip_address_enabled"
              label={t("v8_enable_ip_address", "Enable IP address")}
              onChange={enabled => !enabled && setValue("ip_addresses", "")}
            />
          </Form.Field>
          <Form.Field error={hasIpAddressError}>
            <label>{t("v8_ip_addresses", "IP addresses")}</label>
            <FormHelper.Field.TextArea
              name="ip_addresses"
              textAreaProps={{
                rows: 4,
                disabled: !ipAddressEnabled,
                placeholder: t("v8_one_ip_address_or_subnet_per_line", "One IP address or subnet per line")
              }}
            />
            {hasIpAddressError && (
              <div className="location-overview-field-error">
                {invalidIpAddressLine
                  ? t("v8_line_{{line}}_is_not_a_valid_ip_address_or_subnet", "Line {{line}} is not a valid IP address or subnet").replace(
                      "{{line}}",
                      `${invalidIpAddressLine}`
                    )
                  : null}
              </div>
            )}
          </Form.Field>
        </div>
        <div className="location-overview-column">
          <div className="location-overview-subheading">{t("v8_privacy", "Privacy")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_employee_name_visible" label={t("v8_show_employee_name", "Show employee name")} />
          </Form.Field>
        </div>
        <div className="location-overview-column">
          <div className="location-overview-subheading">{t("v8_notes", "Notes")}</div>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_car_note_enabled" label={t("v8_car_note", "Car note")} />
          </Form.Field>
          <Form.Field>
            <FormHelper.Field.Checkbox name="is_customer_note_enabled" label={t("v8_customer_note", "Customer note")} />
          </Form.Field>
        </div>
      </div>
    </Element>
  );
};
